/**
 * Deep equality comparison for values.
 * Recursively compares primitives, arrays, and objects.
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function isEqual(a: unknown, b: unknown): boolean {
  // Same reference or both primitive and equal
  if (a === b) return true;

  // Handle null/undefined
  if (a == null || b == null) return a === b;

  // Different types
  if (typeof a !== typeof b) return false;

  // Arrays - check BEFORE general object check since arrays are objects
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  // One is array, the other is not (but both are objects)
  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }

  // Objects (but not arrays, which are handled above)
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    // Non-plain objects (e.g. browser API objects like MediaStream, RTCPeerConnection)
    // have no enumerable own keys, so Object.keys() returns []. Two different instances
    // cannot be proven equal by value inspection — fall back to reference inequality.
    // Plain objects ({}) have Object.prototype as their prototype and compare as equal.
    if (keysA.length === 0 && keysB.length === 0) {
      const isPlainA = Object.getPrototypeOf(a) === Object.prototype;
      const isPlainB = Object.getPrototypeOf(b) === Object.prototype;
      return isPlainA && isPlainB;
    }

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => {
      const objA = a as Record<string, unknown>;
      const objB = b as Record<string, unknown>;
      return Object.prototype.hasOwnProperty.call(objB, key) && isEqual(objA[key], objB[key]);
    });
  }

  // Primitives that aren't equal
  return false;
}
